import { todayLocal } from "@/lib/tenure";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { undoableAction } from "@/lib/undo";
import { AddAttendanceDialog } from "@/pages/Attendance";
import { BackupWidget, BulkCollectionsDialog } from "@/pages/LearningHub";
import AttendanceChatClassifier from "@/pages/AttendanceChatClassifier";
import { OrphanedAttendance } from "@/components/OrphanedAttendance";
import { UsageAnalytics } from "@/components/UsageAnalytics";
import { BarChart3 } from "lucide-react";
import DeptChips from "@/components/DeptChips";
import * as XLSX from "xlsx";
import {
    SlidersHorizontal,
    Users,
    Building2,
    UserCog,
    ShieldCheck,
    Plus,
    Pencil,
    Trash2,
    Save,
    X,
    Settings as SettingsIcon,
    Upload,
    Download,
    FileSpreadsheet,
    CheckCircle2,
    AlertCircle,
    PencilLine,
    ChevronDown,
    FileDown,
    Clock,
    GraduationCap,
    Compass,
    Megaphone,
    GripVertical,
    Eye,
    EyeOff,
    Sparkles,
    Loader2,
    Mail,
    BellOff,
    BookOpen,
    UploadCloud,
    RefreshCw,
} from "lucide-react";

const Tab = ({ active, onClick, icon: Icon, label, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
            active
                ? "bg-[#2EB4AC] text-white"
                : "text-slate-600 hover:bg-slate-100"
        }`}
    >
        <Icon className="h-4 w-4" />
        {label}
    </button>
);

const ControlPanel = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "super_admin";
    const canUpload = isAdmin || user?.role === "manager";
    const canMarkKno = isAdmin || user?.role === "coordinator";
    const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const initialTab = urlParams?.get("tab") || "settings";
    const [tab, setTab] = useState(initialTab);
    const { users, depts, settings, refreshAll } = useData();

    const load = async () => { await refreshAll(); };

    // Data is already loaded by DataProvider; nothing extra to fetch.

    return (
        <div className="space-y-6" data-testid="control-panel-page">
            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Configuration</div>
                <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                    <SlidersHorizontal className="h-7 w-7 text-[#2EB4AC]" />
                    Control Panel
                </h1>
                <p className="text-slate-500 mt-1">Manage employees, departments, managers, attendance uploads and PAS cycle settings</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-2 inline-flex flex-wrap gap-1">
                <Tab active={tab==="settings"} onClick={() => setTab("settings")} icon={SettingsIcon} label="PAS Cycle" testid="tab-settings" />
                <Tab active={tab==="roa-cycle"} onClick={() => setTab("roa-cycle")} icon={Compass} label="ROA Cycle" testid="tab-roa-cycle" />
                {canUpload && <Tab active={tab==="attendance"} onClick={() => setTab("attendance")} icon={Clock} label="Attendance" testid="tab-attendance" />}
                {isAdmin && <Tab active={tab==="employees"} onClick={() => setTab("employees")} icon={Users} label="Employees" testid="tab-employees" />}
                {isAdmin && <Tab active={tab==="departments"} onClick={() => setTab("departments")} icon={Building2} label="Departments" testid="tab-departments" />}
                {isAdmin && <Tab active={tab==="managers"} onClick={() => setTab("managers")} icon={UserCog} label="Managers" testid="tab-managers" />}
                {isAdmin && <Tab active={tab==="admins"} onClick={() => setTab("admins")} icon={ShieldCheck} label="Super Admins" testid="tab-admins" />}
                {isAdmin && <Tab active={tab==="ticker"} onClick={() => setTab("ticker")} icon={Megaphone} label="Ticker" testid="tab-ticker" />}
                {isAdmin && <Tab active={tab==="learning-sync"} onClick={() => setTab("learning-sync")} icon={BookOpen} label="Learning · Sync" testid="tab-learning-sync" />}
                {isAdmin && <Tab active={tab==="email-alerts"} onClick={() => setTab("email-alerts")} icon={Mail} label="Email alerts" testid="tab-email-alerts" />}
                <Tab active={tab==="usage"} onClick={() => setTab("usage")} icon={BarChart3} label="Usage Analytics" testid="tab-usage" />
            </div>

            {tab === "settings" && <SettingsTab settings={settings} onChange={load} />}
            {tab === "roa-cycle" && <RoaCycleTab settings={settings} onChange={load} />}
            {tab === "attendance" && canUpload && <AttendanceTab />}
            {tab === "employees" && isAdmin && <EmployeesTab users={users} depts={depts} onChange={load} />}
            {tab === "departments" && isAdmin && <DepartmentsTab depts={depts} onChange={load} />}
            {tab === "managers" && isAdmin && <ManagersTab users={users} onChange={load} />}
            {tab === "admins" && isAdmin && <AdminsTab users={users} onChange={load} />}
            {tab === "ticker" && isAdmin && <TickerTab settings={settings} onChange={load} />}
            {tab === "learning-sync" && isAdmin && <LearningSyncTab user={user} depts={depts} />}
            {tab === "email-alerts" && isAdmin && <EmailAlertsTab settings={settings} users={users} onChange={load} />}
            {tab === "usage" && <UsageAnalytics />}
        </div>
    );
};

const Input = (props) => (
    <input {...props} className={`h-11 w-full px-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white text-slate-900 outline-none transition-all ${props.className || ""}`} />
);

const Select = ({ children, ...props }) => (
    <select {...props} className={`h-11 w-full px-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white text-slate-900 outline-none transition-all ${props.className || ""}`}>
        {children}
    </select>
);

const SettingsTab = ({ settings, onChange }) => {
    const [cycle, setCycle] = useState(45);
    const [start, setStart] = useState("");

    useEffect(() => {
        if (settings) {
            setCycle(settings.cycle_days || 45);
            setStart(settings.start_date || "");
        }
    }, [settings]);

    const save = async () => {
        try {
            await api.put("/settings", { cycle_days: Number(cycle), start_date: start });
            toast.success("PAS cycle updated");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update");
        }
    };

    return (
        <div className="max-w-2xl">
            <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-6">
                <div className="flex items-center gap-2">
                    <SettingsIcon className="h-4 w-4 text-[#25908A]" />
                    <h3 className="font-heading text-lg font-semibold text-slate-900">PAS Cycle Configuration</h3>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                    This cycle length is applied organization-wide when generating PAS sessions. Super Admins and Managers can change this.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Cycle length (days)</label>
                        <Input
                            data-testid="cycle-days-input"
                            type="number"
                            min={1}
                            max={365}
                            value={cycle}
                            onChange={(e) => setCycle(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Cycle start date</label>
                        <Input
                            data-testid="cycle-start-input"
                            type="date"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                        />
                    </div>
                </div>

                <button
                    onClick={save}
                    data-testid="save-cycle-settings"
                    className="mt-6 inline-flex items-center gap-2 h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors"
                >
                    <Save className="h-4 w-4" />
                    Save settings
                </button>
            </div>
        </div>
    );
};

const RoaCycleTab = ({ settings, onChange }) => {
    const [cycle, setCycle] = useState(180);
    const [start, setStart] = useState("");

    useEffect(() => {
        if (settings) {
            setCycle(settings.roa_cycle_days || 180);
            setStart(settings.roa_start_date || "");
        }
    }, [settings]);

    const save = async () => {
        try {
            await api.put("/settings", {
                roa_cycle_days: Number(cycle),
                roa_start_date: start || null,
            });
            toast.success("ROA cycle updated");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update");
        }
    };

    const pasStart = settings?.start_date;
    const effectiveStart = start || pasStart || "—";

    return (
        <div className="max-w-2xl">
            <div className="border-2 border-dashed border-indigo-300 bg-indigo-50/40 rounded-lg p-6">
                <div className="flex items-center gap-2">
                    <Compass className="h-4 w-4 text-indigo-600" />
                    <h3 className="font-heading text-lg font-semibold text-slate-900">ROA Cycle Configuration</h3>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                    Roadmap of Association is a long-arc, 6-month cadence between manager and managee. Super Admins and Managers can change this.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Cycle length (days)</label>
                        <Input
                            data-testid="roa-cycle-days-input"
                            type="number"
                            min={1}
                            max={730}
                            value={cycle}
                            onChange={(e) => setCycle(e.target.value)}
                        />
                        <div className="text-[11px] text-slate-500 mt-1.5">Default 180 days (~6 months).</div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">ROA start date</label>
                        <Input
                            data-testid="roa-cycle-start-input"
                            type="date"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                        />
                        <div className="text-[11px] text-slate-500 mt-1.5">
                            Leave blank to inherit the PAS start date ({pasStart || "not set"}).
                            <span className="block">Effective start: <span className="font-medium text-slate-700">{effectiveStart}</span></span>
                        </div>
                    </div>
                </div>

                <button
                    onClick={save}
                    data-testid="save-roa-cycle-settings"
                    className="mt-6 inline-flex items-center gap-2 h-11 px-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                >
                    <Save className="h-4 w-4" />
                    Save ROA settings
                </button>
            </div>
        </div>
    );
};

const EmployeesTab = ({ users, depts, onChange }) => {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", role: "employee", department_id: "", sub_department: "", position: "", manager_id: "", designation: "", date_of_joining: "", managed_department_ids: [] });

    const reset = () => setForm({ name: "", email: "", role: "employee", department_id: "", sub_department: "", position: "", manager_id: "", designation: "", date_of_joining: "", managed_department_ids: [] });

    const openCreate = () => { reset(); setEditing(null); setOpen(true); };
    const openEdit = (u) => {
        setForm({
            name: u.name || "",
            email: u.email || "",
            role: u.role || "employee",
            department_id: u.department_id || "",
            sub_department: u.sub_department || "",
            position: u.position || "",
            manager_id: u.manager_id || "",
            designation: u.designation || "",
            date_of_joining: u.date_of_joining || "",
            managed_department_ids: Array.isArray(u.managed_department_ids) ? [...u.managed_department_ids] : [],
        });
        setEditing(u);
        setOpen(true);
    };

    const submit = async () => {
        try {
            const payload = {
                name: form.name,
                role: form.role,
                department_id: form.department_id || null,
                sub_department: (form.sub_department || "").trim(),
                position: (form.position || "").trim(),
                manager_id: form.manager_id || null,
                designation: form.designation,
                date_of_joining: form.date_of_joining || "",
                managed_department_ids: (form.managed_department_ids || []).filter((id) => id && id !== form.department_id),
            };
            if (editing) {
                const emailChanged = form.email.trim().toLowerCase() !== (editing.email || "").toLowerCase();
                if (emailChanged && !window.confirm(`Change ${editing.name}'s email to ${form.email.trim()}? All their history stays on this account; they'll sign in with the new email.`)) return;
                await api.put(`/users/${editing.user_id}`, emailChanged ? { ...payload, email: form.email.trim() } : payload);
                toast.success("Employee updated");
            } else {
                await api.post("/users", { ...payload, email: form.email });
                toast.success("Employee created");
            }
            setOpen(false);
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const historyWarning = async (ids) => {
        const fps = await Promise.all(ids.map((id) => api.get(`/users/${id}/footprint`).then((r) => r.data).catch(() => ({}))));
        const months = fps.reduce((a, f) => a + (f.attendance_records || 0), 0);
        const msgs = fps.reduce((a, f) => a + (f.attendance_message_log || 0), 0);
        const kno = fps.reduce((a, f) => a + (f.kno_attendance || 0), 0);
        if (!months && !msgs && !kno) return "";
        return `\n\n⚠ They have attendance history (${months} month records · ${msgs} chat messages · ${kno} KNO entries). Deleting will orphan it.\nIf you only need to fix an email, edit the person instead.`;
    };

    const remove = async (u) => {
        const warn = await historyWarning([u.user_id]);
        if (!window.confirm(`Remove ${u.name}? This cannot be undone.${warn}`)) return;
        try {
            await api.delete(`/users/${u.user_id}`);
            toast.success("Removed");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const deptName = (id) => depts.find((d) => d.id === id)?.name || "—";
    const managerName = (id) => users.find((u) => u.user_id === id)?.name || "—";
    const managers = users.filter((u) => u.role === "manager" || u.role === "super_admin");

    const [selected, setSelected] = useState([]);
    useEffect(() => { setSelected((s) => s.filter((id) => users.some((u) => u.user_id === id))); }, [users]);

    const toggleAll = (checked) => setSelected(checked ? users.map((u) => u.user_id) : []);
    const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const bulkDelete = async () => {
        if (selected.length === 0) return;
        const warn = await historyWarning(selected);
        if (!window.confirm(`Remove ${selected.length} employee(s)? This cannot be undone.${warn}`)) return;
        try {
            await api.post("/users/bulk-delete", { ids: selected });
            toast.success(`Removed ${selected.length} employees`);
            setSelected([]);
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                    {selected.length > 0 && (
                        <>
                            <div className="text-sm text-slate-600 px-3 h-11 inline-flex items-center rounded-lg bg-teal-50 border border-teal-100 text-[#25908A] font-medium">
                                {selected.length} selected
                            </div>
                            <button onClick={bulkDelete} data-testid="bulk-delete-employees-btn" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-colors">
                                <Trash2 className="h-4 w-4" /> Delete selected
                            </button>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <ExportMenu
                        entity="employees"
                        rows={users.map((u) => ({
                            name: u.name || "",
                            email: u.email || "",
                            role: u.role || "",
                            department: depts.find((d) => d.id === u.department_id)?.name || "",
                            sub_department: u.sub_department || "",
                            position: u.position || "",
                            manager_email: users.find((x) => x.user_id === u.manager_id)?.email || "",
                            designation: u.designation || "",
                        }))}
                    />
                    <BulkUploader endpoint="/users" entity="employees" onDone={onChange} />
                    <button onClick={openCreate} data-testid="add-employee-btn" className="inline-flex items-center gap-2 h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors">
                        <Plus className="h-4 w-4" /> Add Employee
                    </button>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            <th className="pl-6 pr-2 py-3 w-10">
                                <input
                                    type="checkbox"
                                    data-testid="select-all-employees"
                                    checked={users.length > 0 && selected.length === users.length}
                                    onChange={(e) => toggleAll(e.target.checked)}
                                    className="h-4 w-4 accent-[#2EB4AC] cursor-pointer"
                                    aria-label="Select all"
                                />
                            </th>
                            <th className="px-6 py-3">Name</th>
                            <th className="px-6 py-3">Email</th>
                            <th className="px-6 py-3">Role</th>
                            <th className="px-6 py-3">Designation</th>
                            <th className="px-6 py-3">Department</th>
                            <th className="px-6 py-3">Manager</th>
                            <th className="px-6 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {users.map((u) => {
                            const checked = selected.includes(u.user_id);
                            return (
                                <tr key={u.user_id} className={`hover:bg-slate-50 ${checked ? "bg-teal-50/40" : ""}`}>
                                    <td className="pl-6 pr-2 py-3">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleOne(u.user_id)}
                                            className="h-4 w-4 accent-[#2EB4AC] cursor-pointer"
                                            aria-label={`Select ${u.name}`}
                                        />
                                    </td>
                                    <td className="px-6 py-3 text-slate-900 font-medium">{u.name}</td>
                                    <td className="px-6 py-3 text-sm text-slate-600">{u.email}</td>
                                    <td className="px-6 py-3 text-sm">{u.role}</td>
                                    <td className="px-6 py-3 text-sm text-slate-700">
                                        {u.designation ? (
                                            <span>{u.designation}</span>
                                        ) : (
                                            <span className="text-slate-400 italic">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-3 text-sm text-slate-600">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <DeptChips user={u} depts={depts} data-testid={`emp-depts-${u.user_id}`} />
                                            {u.sub_department && (
                                                <span
                                                    data-testid={`emp-sub-dept-${u.user_id}`}
                                                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-teal-50 text-[#25908A] border border-teal-100"
                                                >
                                                    {u.sub_department}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 text-sm text-slate-600">{managerName(u.manager_id)}</td>
                                    <td className="px-6 py-3 text-right space-x-1">
                                        <button onClick={() => openEdit(u)} className="inline-flex items-center justify-center h-9 w-9 rounded-lg hover:bg-slate-100 text-slate-600" aria-label="Edit">
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => remove(u)} className="inline-flex items-center justify-center h-9 w-9 rounded-lg hover:bg-red-50 text-red-600" aria-label="Delete">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {open && (
                <Modal title={editing ? "Edit Employee" : "Add Employee"} onClose={() => setOpen(false)}>
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Full name</label>
                            <Input data-testid="emp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Email (must be @wellversed.in)</label>
                            <Input data-testid="emp-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                            {editing && <div className="text-[11px] text-slate-500 mt-1.5">Fix a wrong email here instead of deleting and re-adding — attendance and history stay with this person.</div>}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Role</label>
                                <Select data-testid="emp-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                                    <option value="employee">Employee</option>
                                    <option value="manager">Manager</option>
                                    <option value="coordinator">Coordinator</option>
                                    <option value="super_admin">Super Admin</option>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Designation</label>
                                <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Date of joining</label>
                                <Input type="date" data-testid="emp-doj-input" max={todayLocal()} value={form.date_of_joining} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Department</label>
                                <Select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                                    <option value="">—</option>
                                    {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">
                                    Sub-department <span className="text-slate-400 normal-case font-normal">(optional)</span>
                                </label>
                                <Input
                                    data-testid="emp-sub-department-input"
                                    placeholder="e.g. Platform, Growth, QA"
                                    value={form.sub_department}
                                    onChange={(e) => setForm({ ...form, sub_department: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">
                                Position <span className="text-slate-400 normal-case font-normal">(optional, hierarchy title)</span>
                            </label>
                            <Input
                                data-testid="emp-position-input"
                                list="emp-position-options"
                                placeholder="e.g. Team Lead, Assistant Manager, Professional"
                                value={form.position}
                                onChange={(e) => setForm({ ...form, position: e.target.value })}
                            />
                            <datalist id="emp-position-options">
                                <option value="Manager" />
                                <option value="Assistant Manager" />
                                <option value="Team Lead" />
                                <option value="Senior Professional" />
                                <option value="Professional" />
                                <option value="Junior Professional" />
                                <option value="Intern" />
                            </datalist>
                            <div className="text-[11px] text-slate-500 mt-1.5">Free text · used as a display title in the Org Chart and Managers tab.</div>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Manager</label>
                            <Select value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
                                <option value="">—</option>
                                {managers.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                            </Select>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">
                                Also manages (additional departments)
                            </label>
                            <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-lg min-h-[46px] bg-white" data-testid="managed-depts-picker">
                                {depts
                                    .filter((d) => d.id !== form.department_id)
                                    .map((d) => {
                                        const on = (form.managed_department_ids || []).includes(d.id);
                                        return (
                                            <button
                                                type="button"
                                                key={d.id}
                                                onClick={() => setForm((f) => {
                                                    const cur = new Set(f.managed_department_ids || []);
                                                    if (cur.has(d.id)) cur.delete(d.id); else cur.add(d.id);
                                                    return { ...f, managed_department_ids: Array.from(cur) };
                                                })}
                                                data-testid={`managed-dept-toggle-${d.id}`}
                                                className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs font-medium border transition-colors ${
                                                    on
                                                        ? "bg-indigo-100 text-indigo-800 border-indigo-300"
                                                        : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700"
                                                }`}
                                            >
                                                {d.name}
                                            </button>
                                        );
                                    })}
                                {depts.filter((d) => d.id !== form.department_id).length === 0 && (
                                    <span className="text-xs text-slate-400 italic px-1">No other departments available.</span>
                                )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1.5">
                                Grants this person Learning-Hub curation, attendance visibility and notify-manager emails for the picked departments — on top of their home department.
                            </div>
                        </div>
                    </div>
                    <ModalActions onCancel={() => setOpen(false)} onSave={submit} />
                </Modal>
            )}
        </div>
    );
};

const DepartmentsTab = ({ depts, onChange }) => {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", alias: "", description: "" });

    const openCreate = () => { setForm({ name: "", alias: "", description: "" }); setEditing(null); setOpen(true); };
    const openEdit = (d) => { setForm({ name: d.name, alias: d.alias || "", description: d.description || "" }); setEditing(d); setOpen(true); };

    const submit = async () => {
        try {
            if (editing) {
                await api.put(`/departments/${editing.id}`, form);
                toast.success("Department updated");
            } else {
                await api.post("/departments", form);
                toast.success("Department created");
            }
            setOpen(false);
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const remove = async (d) => {
        if (!window.confirm(`Remove ${d.name}?`)) return;
        await api.delete(`/departments/${d.id}`);
        onChange();
    };

    const [selected, setSelected] = useState([]);
    useEffect(() => { setSelected((s) => s.filter((id) => depts.some((d) => d.id === id))); }, [depts]);
    const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    const toggleAll = (checked) => setSelected(checked ? depts.map((d) => d.id) : []);
    const bulkDelete = async () => {
        if (selected.length === 0) return;
        if (!window.confirm(`Remove ${selected.length} department(s)? Members will be unassigned.`)) return;
        try {
            await api.post("/departments/bulk-delete", { ids: selected });
            toast.success(`Removed ${selected.length} departments`);
            setSelected([]);
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                    {depts.length > 0 && (
                        <label className="inline-flex items-center gap-2 h-11 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 bg-white cursor-pointer hover:border-[#2EB4AC] transition-colors">
                            <input
                                type="checkbox"
                                data-testid="select-all-depts"
                                checked={selected.length === depts.length}
                                onChange={(e) => toggleAll(e.target.checked)}
                                className="h-4 w-4 accent-[#2EB4AC]"
                            />
                            Select all
                        </label>
                    )}
                    {selected.length > 0 && (
                        <>
                            <div className="text-sm text-slate-600 px-3 h-11 inline-flex items-center rounded-lg bg-teal-50 border border-teal-100 text-[#25908A] font-medium">
                                {selected.length} selected
                            </div>
                            <button onClick={bulkDelete} data-testid="bulk-delete-depts-btn" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-colors">
                                <Trash2 className="h-4 w-4" /> Delete selected
                            </button>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <ExportMenu
                        entity="departments"
                        rows={depts.map((d) => ({
                            name: d.name || "",
                            description: d.description || "",
                        }))}
                    />
                    <BulkUploader endpoint="/departments" entity="departments" onDone={onChange} />
                    <button onClick={openCreate} data-testid="add-dept-btn" className="inline-flex items-center gap-2 h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors">
                        <Plus className="h-4 w-4" /> Add Department
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {depts.map((d) => {
                    const checked = selected.includes(d.id);
                    return (
                        <div key={d.id} className={`bg-white border rounded-lg shadow-sm p-5 transition-colors ${checked ? "border-[#2EB4AC] bg-teal-50/40" : "border-slate-200"}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3 min-w-0">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleOne(d.id)}
                                        className="mt-1 h-4 w-4 accent-[#2EB4AC] cursor-pointer"
                                        aria-label={`Select ${d.name}`}
                                    />
                                    <div>
                                        <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                                            {d.name}
                                            {d.alias && (
                                                <span className="text-[10px] uppercase tracking-widest bg-teal-50 text-[#25908A] border border-teal-100 rounded-full px-2 py-0.5 font-bold" title="Alias — shown in badges">
                                                    {d.alias}
                                                </span>
                                            )}
                                        </div>
                                        {d.description && <div className="text-sm text-slate-500 mt-1">{d.description}</div>}
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => openEdit(d)} className="h-9 w-9 rounded-lg hover:bg-slate-100 text-slate-600 flex items-center justify-center"><Pencil className="h-4 w-4" /></button>
                                    <button onClick={() => remove(d)} className="h-9 w-9 rounded-lg hover:bg-red-50 text-red-600 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {open && (
                <Modal title={editing ? "Edit Department" : "Add Department"} onClose={() => setOpen(false)}>
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Name</label>
                            <Input data-testid="dept-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Alias <span className="text-slate-400 normal-case font-normal">(short label shown in badges — falls back to name if blank)</span></label>
                            <Input data-testid="dept-alias-input" value={form.alias} placeholder="e.g. AMS, D2C, SCM" maxLength={16} onChange={(e) => setForm({ ...form, alias: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Description</label>
                            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                    </div>
                    <ModalActions onCancel={() => setOpen(false)} onSave={submit} />
                </Modal>
            )}
        </div>
    );
};

const ManagersTab = ({ users, onChange }) => {
    const managers = users.filter((u) => u.role === "manager" || u.role === "super_admin");

    const assignManagees = async (managerId, empIds) => {
        // Reassign managees
        const current = users.filter((u) => u.manager_id === managerId).map(u=>u.user_id);
        for (const uid of current) {
            if (!empIds.includes(uid)) {
                await api.put(`/users/${uid}`, { manager_id: null });
            }
        }
        for (const uid of empIds) {
            if (!current.includes(uid)) {
                await api.put(`/users/${uid}`, { manager_id: managerId });
            }
        }
        onChange();
    };

    const [editing, setEditing] = useState(null);
    const [selected, setSelected] = useState([]);

    const openAssign = (m) => {
        setEditing(m);
        setSelected(users.filter((u) => u.manager_id === m.user_id).map(u=>u.user_id));
    };

    const toggle = (id) => {
        setSelected(selected.includes(id) ? selected.filter(x=>x!==id) : [...selected, id]);
    };

    const save = async () => {
        await assignManagees(editing.user_id, selected);
        toast.success("Managees updated");
        setEditing(null);
    };

    return (
        <div className="space-y-4">
            <div className="text-sm text-slate-500">Promote users to Manager role from the Employees tab. Assign their managees here.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {managers.map((m) => {
                    const mees = users.filter((u) => u.manager_id === m.user_id);
                    return (
                        <div key={m.user_id} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-heading text-lg font-semibold text-slate-900">{m.name}</div>
                                    <div className="text-sm text-slate-500">{m.email}</div>
                                    <div className="mt-2 text-xs text-slate-500 uppercase tracking-wider font-semibold">{mees.length} managee{mees.length!==1?"s":""}</div>
                                </div>
                                <button onClick={() => openAssign(m)} className="h-10 px-4 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium transition-colors">
                                    Assign managees
                                </button>
                            </div>
                            {mees.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1">
                                    {mees.map((u) => (
                                        <span key={u.user_id} className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200">{u.name}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {editing && (
                <Modal title={`Assign managees to ${editing.name}`} onClose={() => setEditing(null)}>
                    <div className="max-h-80 overflow-y-auto space-y-1 scrollbar-thin">
                        {users.filter(u=>u.user_id !== editing.user_id && u.role === "employee").map((u) => (
                            <label key={u.user_id} className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(u.user_id)}
                                    onChange={() => toggle(u.user_id)}
                                    className="h-4 w-4 accent-[#2EB4AC]"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-slate-900">{u.name}</div>
                                    <div className="text-xs text-slate-500">{u.email}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <ModalActions onCancel={() => setEditing(null)} onSave={save} />
                </Modal>
            )}
        </div>
    );
};

const AdminsTab = ({ users, onChange }) => {
    const admins = users.filter((u) => u.role === "super_admin");

    const demote = async (u) => {
        if (!window.confirm(`Demote ${u.name} to Employee?`)) return;
        await api.put(`/users/${u.user_id}`, { role: "employee" });
        toast.success("Demoted");
        onChange();
    };

    const promote = async (u) => {
        await api.put(`/users/${u.user_id}`, { role: "super_admin" });
        toast.success(`${u.name} is now Super Admin`);
        onChange();
    };

    return (
        <div className="space-y-4">
            <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-4 text-sm text-slate-700">
                Super Admins can view and manage everything across the organization. Promote from any logged-in @wellversed.in user below.
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Current Super Admins
                </div>
                <div className="divide-y divide-slate-100">
                    {admins.map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between px-6 py-3">
                            <div>
                                <div className="font-medium text-slate-900">{u.name}</div>
                                <div className="text-xs text-slate-500">{u.email}</div>
                            </div>
                            <button onClick={() => demote(u)} className="h-9 px-3 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors">Demote</button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Other users — promote to Super Admin
                </div>
                <div className="divide-y divide-slate-100">
                    {users.filter(u => u.role !== "super_admin").map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between px-6 py-3">
                            <div>
                                <div className="font-medium text-slate-900">{u.name}</div>
                                <div className="text-xs text-slate-500">{u.email}</div>
                            </div>
                            <button onClick={() => promote(u)} data-testid={`promote-${u.user_id}`} className="h-9 px-3 rounded-lg text-sm text-[#25908A] hover:bg-teal-50 transition-colors">Promote</button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const Modal = ({ title, onClose, children }) => createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200">
                <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
                <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" aria-label="Close">
                    <X className="h-4 w-4" />
                </button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>,
    document.body
);

const ModalActions = ({ onCancel, onSave }) => (
    <div className="mt-6 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="h-11 px-5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-700 font-medium transition-colors">Cancel</button>
        <button onClick={onSave} data-testid="modal-save-btn" className="inline-flex items-center gap-2 h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors">
            <Save className="h-4 w-4" /> Save
        </button>
    </div>
);

/**
 * Elegant bulk uploader with template download.
 * endpoint: "/users" or "/departments"
 * entity: "employees" or "departments"
 */
/**
 * Elegant bulk uploader with:
 *  - Template download (CSV)
 *  - CSV or XLSX upload
 *  - Preview-before-import (dry_run) showing row-level create/update/error actions
 *  - Confirm Import step to actually persist
 */
const BulkUploader = ({ endpoint, entity, onDone }) => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);            // Blob/File ready to send (CSV)
    const [originalFileName, setOriginalFileName] = useState("");
    const [dragging, setDragging] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);       // dry-run response
    const [committed, setCommitted] = useState(false);
    const inputRef = useRef(null);

    const downloadTemplate = async () => {
        try {
            const r = await fetch(`${API}${endpoint}/template`, {
                credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("pas_session_token") || ""}` },
            });
            if (!r.ok) throw new Error("Failed to download template");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const ext = entity === "attendance" || entity === "kno" ? "xlsx" : "csv";
            a.download = `${entity}_template.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Template downloaded");
        } catch (e) {
            toast.error(e.message || "Failed");
        }
    };

    // Convert XLSX → CSV so backend stays CSV-only.
    const xlsxToCsvBlob = async (xlsxFile) => {
        const buf = await xlsxFile.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const first = wb.Sheets[wb.SheetNames[0]];
        const csv = XLSX.utils.sheet_to_csv(first);
        return new Blob([csv], { type: "text/csv" });
    };

    const handleFile = async (f) => {
        if (!f) return;
        const name = (f.name || "").toLowerCase();
        try {
            let csvFile;
            if (name.endsWith(".csv")) {
                csvFile = f;
            } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
                const blob = await xlsxToCsvBlob(f);
                csvFile = new File([blob], f.name.replace(/\.xlsx?$/i, ".csv"), { type: "text/csv" });
            } else {
                toast.error("Please upload a .csv or .xlsx file");
                return;
            }
            setFile(csvFile);
            setOriginalFileName(f.name);
            setPreview(null);
            setCommitted(false);
            // auto-preview
            await runPreview(csvFile);
        } catch (e) {
            toast.error("Failed to parse file");
        }
    };

    const runPreview = async (csvFile) => {
        setPreviewing(true);
        try {
            const fd = new FormData();
            fd.append("file", csvFile);
            const r = await fetch(`${API}${endpoint}/bulk?dry_run=true`, {
                method: "POST",
                body: fd,
                credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("pas_session_token") || ""}` },
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data?.detail || "Preview failed");
            setPreview(data);
        } catch (e) {
            toast.error(e.message || "Failed");
        } finally {
            setPreviewing(false);
        }
    };

    const commit = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await fetch(`${API}${endpoint}/bulk`, {
                method: "POST",
                body: fd,
                credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("pas_session_token") || ""}` },
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data?.detail || "Upload failed");
            const created = data.created || 0;
            const updated = data.updated || 0;
            const errors = (data.errors || []).length;
            if (errors === 0) toast.success(`Imported ${created} new · updated ${updated}`);
            else toast.warning(`Imported ${created} · updated ${updated} · ${errors} skipped`);
            setPreview(data);
            setCommitted(true);
            onDone?.();
        } catch (e) {
            toast.error(e.message || "Failed");
        } finally {
            setUploading(false);
        }
    };

    const reset = () => {
        setFile(null);
        setOriginalFileName("");
        setPreview(null);
        setCommitted(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const close = () => { setOpen(false); reset(); };

    const headers = entity === "employees"
        ? ["Row", "Name", "Email", "Role", "Department", "Manager"]
        : entity === "attendance"
        ? ["Row", "Employee", "Department", "Month", "Late ≥10:30", "Early <18:00", "Total", "OFF", "WFH"]
        : entity === "kno"
        ? ["Row", "Employee", "Session date", "Status", "Action"]
        : ["Row", "Name", "Description"];

    const renderRow = (r) => {
        if (entity === "employees") {
            return [r.row, r.name, r.email, r.role, r.department, r.manager_email];
        }
        if (entity === "attendance") {
            return [
                r.row,
                r.employee_name || r.employee,
                r.department,
                r.month || r.month_raw,
                r.late_after_1030 ?? "",
                r.early_before_1800 ?? "",
                r.total_instances ?? "",
                r.oof ?? "",
                r.wfh ?? "",
            ];
        }
        if (entity === "kno") {
            return [r.row, r.employee, r.session_date, r.status, r.action];
        }
        return [r.row, r.name, r.description];
    };

    const actionBadge = (action) => {
        if (action === "create") return { cls: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2, label: "New" };
        if (action === "update") return { cls: "bg-blue-100 text-blue-800 border-blue-200", icon: PencilLine, label: "Update" };
        return { cls: "bg-red-100 text-red-800 border-red-200", icon: AlertCircle, label: "Error" };
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid={`bulk-upload-${entity}-btn`}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-slate-700 bg-white font-medium transition-colors"
            >
                <Upload className="h-4 w-4" />
                Bulk upload
            </button>

            {open && (
                <ModalWide title={`Bulk import ${entity}`} onClose={close}>
                    <div className="space-y-5">
                        {!committed && (
                            <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-4 flex items-start gap-3">
                                <FileSpreadsheet className="h-4 w-4 text-[#25908A] mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-900">Start with the template</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Download the CSV, fill rows, then upload (CSV or XLSX). You'll preview changes before anything is saved.
                                    </div>
                                </div>
                                <button
                                    onClick={downloadTemplate}
                                    data-testid={`download-${entity}-template`}
                                    className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-white border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium transition-colors flex-shrink-0"
                                >
                                    <Download className="h-4 w-4" />
                                    Template
                                </button>
                            </div>
                        )}

                        {!preview && !previewing && (
                            <div
                                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                                onClick={() => inputRef.current?.click()}
                                className={`rounded-lg p-8 text-center cursor-pointer transition-colors ${
                                    dragging ? "border-2 border-dashed border-[#2EB4AC] bg-teal-50" : "border-2 border-dashed border-slate-300 bg-slate-50 hover:border-[#2EB4AC]"
                                }`}
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    className="hidden"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                />
                                <Upload className="h-8 w-8 text-slate-400 mx-auto" />
                                <div className="mt-3 text-sm font-medium text-slate-700">Drop CSV or XLSX here or click to browse</div>
                                <div className="text-xs text-slate-500 mt-1">UTF-8 encoded · up to 2MB</div>
                            </div>
                        )}

                        {previewing && (
                            <div className="text-center py-10 text-slate-500 text-sm">Analyzing {originalFileName}…</div>
                        )}

                        {preview && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                        <FileSpreadsheet className="h-4 w-4 text-[#25908A]" />
                                        <div className="text-sm font-medium text-slate-900 truncate">{originalFileName}</div>
                                        {!committed && (
                                            <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-900 underline ml-2">Change file</button>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200">
                                            <CheckCircle2 className="h-3 w-3" /> {preview.created} new
                                        </span>
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
                                            <PencilLine className="h-3 w-3" /> {preview.updated} updates
                                        </span>
                                        {(preview.errors || []).length > 0 && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-red-100 text-red-800 border-red-200">
                                                <AlertCircle className="h-3 w-3" /> {preview.errors.length} errors
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {!committed && preview.dry_run && (
                                    <div className="text-xs text-slate-500 border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg px-3 py-2">
                                        Preview only — nothing has been saved yet. Review rows below, then click <span className="font-medium text-slate-900">Confirm import</span>.
                                    </div>
                                )}

                                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto scrollbar-thin">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                                            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                                                <th className="px-3 py-2 w-24">Status</th>
                                                {headers.map((h) => (
                                                    <th key={h} className="px-3 py-2">{h}</th>
                                                ))}
                                                <th className="px-3 py-2">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {(preview.rows || []).map((r, i) => {
                                                const b = actionBadge(r.action);
                                                const Icon = b.icon;
                                                const cells = renderRow(r);
                                                return (
                                                    <tr key={i} className={r.action === "error" ? "bg-red-50/40" : ""}>
                                                        <td className="px-3 py-2">
                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${b.cls}`}>
                                                                <Icon className="h-3 w-3" />
                                                                {b.label}
                                                            </span>
                                                        </td>
                                                        {cells.map((c, j) => (
                                                            <td key={j} className="px-3 py-2 text-slate-700 truncate max-w-[160px]">{c || "—"}</td>
                                                        ))}
                                                        <td className="px-3 py-2 text-xs text-red-700">{r.error || ""}</td>
                                                    </tr>
                                                );
                                            })}
                                            {(!preview.rows || preview.rows.length === 0) && (
                                                <tr><td colSpan={headers.length + 2} className="px-3 py-6 text-center text-slate-500">No rows in file</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {committed && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 font-medium inline-flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4" /> Import complete — {preview.created} created · {preview.updated} updated.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex items-center justify-end gap-2">
                        <button onClick={close} className="h-11 px-5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-700 font-medium transition-colors">
                            {committed ? "Close" : "Cancel"}
                        </button>
                        {preview && !committed && (preview.created + preview.updated) > 0 && (
                            <button
                                onClick={commit}
                                disabled={uploading}
                                data-testid={`confirm-upload-${entity}`}
                                className="inline-flex items-center gap-2 h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Upload className="h-4 w-4" />
                                {uploading ? "Importing…" : `Confirm import (${preview.created + preview.updated})`}
                            </button>
                        )}
                    </div>
                </ModalWide>
            )}
        </>
    );
};

/** Wider modal variant for preview tables */
const ModalWide = ({ title, onClose, children }) => createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200 sticky top-0 bg-white">
                <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
                <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" aria-label="Close">
                    <X className="h-4 w-4" />
                </button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>,
    document.body
);

/** Export menu: CSV or XLSX using the passed-in rows */
const ExportMenu = ({ entity, rows }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const today = new Date().toISOString().slice(0, 10);
    const filename = `${entity}_${today}`;

    const exportCSV = () => {
        if (!rows?.length) { toast.info("Nothing to export"); return; }
        const headers = Object.keys(rows[0]);
        const escape = (v) => {
            const s = v == null ? "" : String(v);
            return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
        const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
        triggerDownload(blob, `${filename}.csv`);
        setOpen(false);
        toast.success("CSV exported");
    };

    const exportXLSX = () => {
        if (!rows?.length) { toast.info("Nothing to export"); return; }
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, entity.slice(0, 31));
        XLSX.writeFile(wb, `${filename}.xlsx`);
        setOpen(false);
        toast.success("XLSX exported");
    };

    const triggerDownload = (blob, name) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={`export-${entity}-btn`}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-slate-700 bg-white font-medium transition-colors"
            >
                <FileDown className="h-4 w-4" />
                Export
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-30 overflow-hidden">
                    <button
                        onClick={exportCSV}
                        data-testid={`export-${entity}-csv`}
                        className="w-full flex items-center gap-2 px-3 h-10 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                        <FileSpreadsheet className="h-4 w-4 text-slate-500" /> Export as CSV
                    </button>
                    <button
                        onClick={exportXLSX}
                        data-testid={`export-${entity}-xlsx`}
                        className="w-full flex items-center gap-2 px-3 h-10 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                    >
                        <FileSpreadsheet className="h-4 w-4 text-[#25908A]" /> Export as XLSX
                    </button>
                </div>
            )}
        </div>
    );
};

const ATTENDANCE_EXPORT_HEADERS = [
    "Employees",
    "Department",
    "Month",
    "Shift",
    "Late Slab 1",
    "Late Slab 2",
    "Late Slab 3",
    "Early Slab 1",
    "Early Slab 2",
    "Early Slab 3",
    "Total Instances",
    "OOF",
    "WFH",
];

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonthTemplate = (ym) => {
    if (!ym || !ym.includes("-")) return ym || "";
    const [y, m] = ym.split("-");
    const idx = parseInt(m, 10) - 1;
    if (idx < 0 || idx > 11) return ym;
    return `${MONTH_SHORT[idx]}-${String(y).slice(-2)}`;
};

const buildAttendanceRow = (r) => {
    const first = (r.employee_name || "").trim().split(" ")[0] || "";
    // Prefer shift-agnostic slab keys (populated by the new chat-publish
    // path); fall back to the legacy corporate-only fields so older rows
    // still export correctly.
    const l1 = r.late_slab_1 ?? r.late_1000_1015 ?? 0;
    const l2 = r.late_slab_2 ?? r.late_1015_1030 ?? 0;
    const l3 = r.late_slab_3 ?? r.late_after_1030 ?? 0;
    const e1 = r.early_slab_1 ?? r.early_1800_1830 ?? 0;
    const e2 = r.early_slab_2 ?? r.early_1830_1900 ?? 0;
    const e3 = r.early_slab_3 ?? r.early_before_1800 ?? 0;
    return [
        first,
        r.department_name || "",
        fmtMonthTemplate(r.month || ""),
        r.shift === "firmware" ? "Firmware" : "Corporate",
        l1, l2, l3, e1, e2, e3,
        r.total_instances || 0,
        r.oof || 0,
        r.wfh || 0,
    ];
};

/** Export menu — visible (client-side XLSX) or all in-scope (server) */
const ExportAttendanceMenu = ({ filtered, monthFilter, fmtMonth }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const exportVisible = () => {
        try {
            if (!filtered || filtered.length === 0) {
                toast.error("No records to export");
                return;
            }
            const aoa = [ATTENDANCE_EXPORT_HEADERS, ...filtered.map(buildAttendanceRow)];
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws["!cols"] = [16, 34, 12, 18, 18, 12, 18, 18, 12, 16, 8, 8].map((w) => ({ wch: w }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Attendance");
            const scopeLabel = monthFilter === "all" ? "visible" : fmtMonth(monthFilter).replace(/\s+/g, "-");
            XLSX.writeFile(wb, `attendance_${scopeLabel}.xlsx`);
            toast.success(`Exported ${filtered.length} record${filtered.length === 1 ? "" : "s"}`);
            setOpen(false);
        } catch (err) {
            toast.error("Export failed");
        }
    };

    const exportAll = async () => {
        try {
            setBusy(true);
            const params = monthFilter !== "all" ? { month: monthFilter } : {};
            const res = await api.get("/attendance/export", { params, responseType: "blob" });
            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `attendance_export_${monthFilter === "all" ? "all" : monthFilter}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Exported all in-scope records");
            setOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Export failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative" ref={menuRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={busy}
                data-testid="cp-attendance-export-btn"
                className="inline-flex items-center gap-2 h-11 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium text-sm transition-colors disabled:opacity-60"
                title="Export attendance records"
            >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                Export
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </button>
            {open && (
                <div
                    className="absolute right-0 top-full mt-2 z-30 w-64 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden"
                    data-testid="cp-attendance-export-menu"
                >
                    <button
                        type="button"
                        onClick={exportVisible}
                        data-testid="cp-attendance-export-visible"
                        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-start gap-2.5 border-b border-slate-100"
                    >
                        <FileSpreadsheet className="h-4 w-4 mt-0.5 text-[#2EB4AC] shrink-0" />
                        <div>
                            <div className="text-sm font-medium text-slate-900">Export visible</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                {filtered.length} row{filtered.length === 1 ? "" : "s"}
                                {monthFilter !== "all" ? ` · ${fmtMonth(monthFilter)}` : " · current view"}
                            </div>
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={exportAll}
                        disabled={busy}
                        data-testid="cp-attendance-export-all"
                        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-start gap-2.5 disabled:opacity-60"
                    >
                        <Download className="h-4 w-4 mt-0.5 text-slate-700 shrink-0" />
                        <div>
                            <div className="text-sm font-medium text-slate-900">Export all (in scope)</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                {monthFilter === "all"
                                    ? "Every month you can see"
                                    : `Full server data for ${fmtMonth(monthFilter)}`}
                            </div>
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

/** Confirmation dialog for sending attendance rollup emails.
 * Runs dry-run first to preview counts, then sends on confirm.
 */
const NotifyManagersDialog = ({ month, fmtMonth, onClose }) => {
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(true);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [emailPreview, setEmailPreview] = useState(null);  // { kind, html, to, cc, subject, name, team_size }
    const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
    // Recipient selection — start with everyone checked once the dry-run preview lands
    const [selectedUsers, setSelectedUsers] = useState(null);       // Set<user_id> | null (null=all)
    const [selectedManagers, setSelectedManagers] = useState(null); // Set<manager_id> | null (null=all)

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await api.post("/attendance/notify", { month, dry_run: true });
                if (!cancel) {
                    setPreview(r.data);
                    setSelectedUsers(new Set((r.data.one_on_ones_preview || []).map(o => o.user_id).filter(Boolean)));
                    setSelectedManagers(new Set((r.data.team_emails_preview || []).map(t => t.manager_id).filter(Boolean)));
                }
            } catch (e) {
                if (!cancel) {
                    toast.error(e?.response?.data?.detail || "Preview failed");
                    onClose();
                }
            } finally {
                if (!cancel) setPreviewing(false);
            }
        })();
        return () => { cancel = true; };
    }, [month, onClose]);

    const toggleUser = (uid) => {
        setSelectedUsers((s) => {
            const next = new Set(s || []);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
        });
    };
    const toggleManager = (mid) => {
        setSelectedManagers((s) => {
            const next = new Set(s || []);
            if (next.has(mid)) next.delete(mid); else next.add(mid);
            return next;
        });
    };
    const setAllUsers = (checked) => {
        setSelectedUsers(new Set(checked ? (preview?.one_on_ones_preview || []).map(o => o.user_id).filter(Boolean) : []));
    };
    const setAllManagers = (checked) => {
        setSelectedManagers(new Set(checked ? (preview?.team_emails_preview || []).map(t => t.manager_id).filter(Boolean) : []));
    };
    const selectedUserCount = selectedUsers ? selectedUsers.size : 0;
    const selectedManagerCount = selectedManagers ? selectedManagers.size : 0;
    const totalSelected = selectedUserCount + selectedManagerCount;

    const doSend = async () => {
        try {
            setSending(true);
            const body = {
                month,
                dry_run: false,
                user_ids: Array.from(selectedUsers || []),
                manager_ids: Array.from(selectedManagers || []),
            };
            const r = await api.post("/attendance/notify", body);
            setResult(r.data);
            const errs = (r.data?.errors || []).length;
            if (errs === 0) {
                toast.success(`Sent ${r.data.one_on_one_sent} personal + ${r.data.team_sent} team emails`);
            } else {
                toast.warning(`Sent with ${errs} error${errs === 1 ? "" : "s"} — see details`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    const openEmailPreview = async (kind, userId) => {
        try {
            setLoadingEmailPreview(true);
            const params = { month, kind };
            if (userId) params.user_id = userId;
            const r = await api.get("/attendance/notify/preview", { params });
            setEmailPreview({ kind, ...r.data });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setLoadingEmailPreview(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[200] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="notify-managers-dialog">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-heading text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                            <Mail className="h-5 w-5 text-[#2EB4AC]" />
                            Notify managers — {fmtMonth(month)}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                            One consolidated email per manager · One personal email per employee with their manager on CC
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="notify-managers-close"
                        className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 inline-flex items-center justify-center"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {previewing ? (
                        <div className="flex items-center justify-center gap-3 py-10 text-slate-500 text-sm">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Computing dispatch plan…
                        </div>
                    ) : result ? (
                        <div className="space-y-3" data-testid="notify-managers-result">
                            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                                <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                                    <CheckCircle2 className="h-5 w-5" />
                                    Emails dispatched
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-3 text-center">
                                    <div className="bg-white rounded-lg py-2.5 border border-emerald-100">
                                        <div className="text-2xl font-bold text-emerald-700 tabular-nums">{result.one_on_one_sent}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Personal emails</div>
                                    </div>
                                    <div className="bg-white rounded-lg py-2.5 border border-emerald-100">
                                        <div className="text-2xl font-bold text-emerald-700 tabular-nums">{result.team_sent}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Team summaries</div>
                                    </div>
                                </div>
                            </div>
                            {(result.errors || []).length > 0 && (
                                <div className="rounded-xl bg-rose-50 border border-rose-100 p-4">
                                    <div className="text-rose-700 font-semibold text-sm mb-2">
                                        {result.errors.length} error{result.errors.length === 1 ? "" : "s"}
                                    </div>
                                    <ul className="space-y-1 max-h-40 overflow-y-auto text-xs text-rose-800">
                                        {result.errors.map((e, i) => (
                                            <li key={i} className="font-mono">
                                                [{e.kind}] {e.to} — {e.error}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ) : preview ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-xl bg-teal-50 border border-teal-100 p-4 text-center">
                                    <div className="text-3xl font-bold text-[#25908A] tabular-nums">
                                        {preview.one_on_one_count}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold mt-1">
                                        Personal emails
                                    </div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">to each employee · manager on CC</div>
                                </div>
                                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4 text-center">
                                    <div className="text-3xl font-bold text-indigo-700 tabular-nums">
                                        {preview.team_email_count}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold mt-1">
                                        Team summaries
                                    </div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">consolidated · to each manager</div>
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 overflow-hidden">
                                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={selectedManagerCount === (preview.team_email_count || 0) && preview.team_email_count > 0}
                                            ref={(el) => { if (el) el.indeterminate = selectedManagerCount > 0 && selectedManagerCount < (preview.team_email_count || 0); }}
                                            onChange={(e) => setAllManagers(e.target.checked)}
                                            data-testid="select-all-team"
                                            className="h-3.5 w-3.5 accent-indigo-600 cursor-pointer shrink-0"
                                            aria-label="Select all team recipients"
                                        />
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">
                                            Team summaries · {selectedManagerCount}/{preview.team_email_count} selected
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => openEmailPreview("team")}
                                        disabled={loadingEmailPreview || preview.team_email_count === 0}
                                        data-testid="preview-team-email-btn"
                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50 shrink-0"
                                    >
                                        <Eye className="h-3 w-3" />
                                        Preview
                                    </button>
                                </div>
                                <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                                    {(preview.team_emails_preview || []).map((t) => {
                                        const picked = selectedManagers?.has(t.manager_id);
                                        return (
                                            <div
                                                key={t.to}
                                                className={`px-3 py-1.5 text-xs flex items-center gap-2 group hover:bg-slate-50 transition-colors ${picked ? "" : "opacity-50"}`}
                                                data-testid={`team-recipient-${t.manager_id}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!picked}
                                                    onChange={() => toggleManager(t.manager_id)}
                                                    className="h-3.5 w-3.5 accent-indigo-600 cursor-pointer shrink-0"
                                                    aria-label={`Toggle ${t.to}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => openEmailPreview("team", t.manager_id)}
                                                    disabled={loadingEmailPreview}
                                                    className="flex-1 min-w-0 flex items-center justify-between text-left"
                                                    data-testid={`preview-team-${t.manager_id}`}
                                                    title="Click to preview this manager's team email"
                                                >
                                                    <span className="text-slate-700 truncate group-hover:text-indigo-700 group-hover:underline">
                                                        {t.to}
                                                    </span>
                                                    <span className="text-slate-500 text-[10px] shrink-0 ml-2">{t.team_size} {t.team_size === 1 ? "member" : "members"}</span>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 overflow-hidden">
                                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={selectedUserCount === (preview.one_on_one_count || 0) && preview.one_on_one_count > 0}
                                            ref={(el) => { if (el) el.indeterminate = selectedUserCount > 0 && selectedUserCount < (preview.one_on_one_count || 0); }}
                                            onChange={(e) => setAllUsers(e.target.checked)}
                                            data-testid="select-all-personal"
                                            className="h-3.5 w-3.5 accent-[#2EB4AC] cursor-pointer shrink-0"
                                            aria-label="Select all personal recipients"
                                        />
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">
                                            Personal emails · {selectedUserCount}/{preview.one_on_one_count} selected
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => openEmailPreview("one_on_one")}
                                        disabled={loadingEmailPreview || preview.one_on_one_count === 0}
                                        data-testid="preview-personal-email-btn"
                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#25908A] hover:text-[#2EB4AC] disabled:opacity-50 shrink-0"
                                    >
                                        <Eye className="h-3 w-3" />
                                        Preview
                                    </button>
                                </div>
                                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                                    {(preview.one_on_ones_preview || []).map((o) => {
                                        const picked = selectedUsers?.has(o.user_id);
                                        return (
                                            <div
                                                key={o.to}
                                                className={`px-3 py-1.5 text-xs flex items-start gap-2 group hover:bg-slate-50 transition-colors ${picked ? "" : "opacity-50"}`}
                                                data-testid={`personal-recipient-${o.user_id}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!picked}
                                                    onChange={() => toggleUser(o.user_id)}
                                                    className="h-3.5 w-3.5 accent-[#2EB4AC] cursor-pointer mt-0.5 shrink-0"
                                                    aria-label={`Toggle ${o.to}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => openEmailPreview("one_on_one", o.user_id)}
                                                    disabled={loadingEmailPreview}
                                                    className="flex-1 min-w-0 text-left"
                                                    data-testid={`preview-personal-${o.user_id}`}
                                                    title="Click to preview this employee's personal email"
                                                >
                                                    <div className="text-slate-700 truncate group-hover:text-[#25908A] group-hover:underline">
                                                        {o.name || o.to}
                                                    </div>
                                                    <div className="text-slate-400 text-[10px] mt-0.5 truncate">
                                                        {o.to}
                                                        {o.cc && <span className="ml-2">· CC: {o.cc}</span>}
                                                    </div>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="notify-managers-cancel"
                        className="h-10 px-4 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium"
                    >
                        {result ? "Close" : "Cancel"}
                    </button>
                    {!result && (
                        <button
                            type="button"
                            onClick={doSend}
                            disabled={previewing || sending || !preview || totalSelected === 0}
                            data-testid="notify-managers-confirm"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            {sending ? "Sending…" : totalSelected === 0 ? "Nothing selected" : `Send ${totalSelected} email${totalSelected === 1 ? "" : "s"}`}
                        </button>
                    )}
                </div>
            </div>

            {emailPreview && (
                <div
                    className="fixed inset-0 z-[210] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="email-preview-modal"
                    onClick={() => setEmailPreview(null)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-5 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                    {emailPreview.kind === "team" ? "Team summary — sample" : "Personal email — sample"}
                                </div>
                                <div className="font-heading font-semibold text-slate-900 text-sm truncate">{emailPreview.subject}</div>
                                <div className="text-xs text-slate-500 mt-0.5 truncate">
                                    <span className="font-medium">TO:</span> {emailPreview.to}
                                    {emailPreview.cc && <span className="ml-2"><span className="font-medium">CC:</span> {emailPreview.cc}</span>}
                                </div>
                            </div>
                            <button
                                onClick={() => setEmailPreview(null)}
                                data-testid="email-preview-close"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 inline-flex items-center justify-center shrink-0"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <iframe
                            title="Email preview"
                            srcDoc={emailPreview.html}
                            sandbox=""
                            className="flex-1 w-full bg-slate-50"
                            data-testid="email-preview-iframe"
                        />
                        <div className="px-5 py-2 border-t border-slate-100 bg-slate-50/60 text-[10px] text-slate-500">
                            This is a live preview of the exact HTML your recipients will receive. Rendered in an isolated sandbox.
                        </div>
                    </div>
                </div>
            )}
        </div>,
        document.body,
    );
};

/** Attendance tab — monthly in/out rollup upload + browse */

const AttendanceTab = () => {
    const { user } = useAuth();
    const isSuperAdminHere = user?.role === "super_admin";
    const { users } = useData();
    const [records, setRecords] = useState([]);
    const [months, setMonths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [monthFilter, setMonthFilter] = useState("all");
    const [picked, setPicked] = useState([]);
    // Stable per-row key. Legacy rows can miss `id` (pre-backfill era) —
    // if we used `r.id` directly, several rows would share `undefined` and
    // `picked.includes(undefined)` would turn every checkbox on the moment
    // one was clicked ("selecting one selects all"). Fall back to a
    // composite key so selection stays row-scoped even if id is missing.
    const rowKey = (r) => r?.id || (r ? `${r.user_id || "?"}::${r.month || "?"}` : "");
    const [focused, setFocused] = useState(0);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showNotifyDialog, setShowNotifyDialog] = useState(false);
    // BETA sub-mode: "classic" (existing XLSX upload + browse) or "chat"
    // (WhatsApp chat-export classifier). Super-admin only for now.
    const [subMode, setSubMode] = useState("classic");
    const tableRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const [r, m] = await Promise.all([
                api.get("/attendance"),
                api.get("/attendance/months"),
            ]);
            setRecords(r.data || []);
            setMonths(m.data?.months || []);
            setPicked([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = monthFilter === "all" ? records : records.filter((r) => r.month === monthFilter);
    const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const fmtMonth = (ym) => { if (!ym) return "—"; const [y, m] = ym.split("-"); return `${MONTH_NAMES[parseInt(m,10)-1]} ${y}`; };

    const togglePick = (key) => setPicked((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));
    const toggleAll = () => {
        const allKeys = filtered.map(rowKey);
        if (picked.length === allKeys.length) setPicked([]);
        else setPicked(allKeys);
    };

    const confirmDelete = async (row) => {
        const id = row?.id;
        if (!id) {
            // Legacy row from a very old upload with no `id` field. The
            // backend startup backfill will assign one on next boot;
            // meanwhile skip so we don't hit /attendance/undefined → 404.
            toast.error("This record has no id yet. Please refresh — a backfill will run on next reload.");
            return;
        }
        const prev = records;
        setRecords((rs) => rs.filter((r) => r.id !== id));
        undoableAction({
            label: "Attendance record deleted",
            description: "This will be permanently removed in 5 seconds.",
            delayMs: 5000,
            onCommit: async () => {
                await api.delete(`/attendance/${id}`);
                await load();
            },
            onUndo: () => setRecords(prev),
        });
    };

    const bulkDelete = async () => {
        if (!picked.length) return;
        // `picked` is a list of composite ROW KEYS (id-or-fallback) so
        // the checkbox stays row-scoped even for legacy rows without an
        // id. Convert to real DB ids here — legacy rows are skipped with
        // a friendly toast rather than sending `undefined` to the API.
        const pickedRows = filtered.filter((r) => picked.includes(rowKey(r)));
        const ids = pickedRows.map((r) => r.id).filter(Boolean);
        if (ids.length === 0) {
            toast.error("None of the selected rows have a stable id yet. Please refresh and try again.");
            return;
        }
        const prev = records;
        setRecords((rs) => rs.filter((r) => !ids.includes(r.id)));
        setPicked([]);
        undoableAction({
            label: `${ids.length} record${ids.length !== 1 ? "s" : ""} deleted`,
            description: "This will be permanently removed in 5 seconds.",
            delayMs: 5000,
            onCommit: async () => {
                await api.post("/attendance/bulk-delete", { ids });
                await load();
            },
            onUndo: () => setRecords(prev),
        });
    };

    const allPicked = filtered.length > 0 && picked.length === filtered.length;
    const somePicked = picked.length > 0 && !allPicked;

    // Keyboard navigation: ArrowUp/Down moves focus, Space toggles selection, Enter has no default target
    useEffect(() => {
        const onKey = (e) => {
            if (!tableRef.current || !tableRef.current.contains(document.activeElement) && document.activeElement !== document.body) {
                if (!tableRef.current?.matches(":hover")) return;
            }
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setFocused((i) => Math.min(filtered.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setFocused((i) => Math.max(0, i - 1));
            } else if (e.key === " ") {
                e.preventDefault();
                const r = filtered[focused];
                if (r) togglePick(rowKey(r));
            } else if (e.key === "Escape" && picked.length > 0) {
                e.preventDefault();
                setPicked([]);
            } else if ((e.key === "a" || e.key === "A") && (e.ctrlKey || e.metaKey)) {
                // Only intercept if user is inside this table area
                if (tableRef.current?.matches(":hover")) {
                    e.preventDefault();
                    toggleAll();
                }
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line
    }, [filtered, focused, picked]);

    return (
        <div className="space-y-4" data-testid="attendance-tab">
            {isSuperAdminHere && (
                <div
                    role="tablist"
                    aria-label="Attendance mode"
                    className="inline-flex items-center h-10 p-1 rounded-xl bg-slate-100"
                    data-testid="attendance-sub-toggle"
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={subMode === "classic"}
                        onClick={() => setSubMode("classic")}
                        data-testid="attendance-sub-classic"
                        className={`inline-flex items-center h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${subMode === "classic" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                    >
                        Classic upload
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={subMode === "chat"}
                        onClick={() => setSubMode("chat")}
                        data-testid="attendance-sub-chat"
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${subMode === "chat" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                    >
                        Chat classifier
                        <span className="inline-flex items-center px-1 h-3.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white text-[8px] font-bold uppercase tracking-wider">
                            Beta
                        </span>
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={subMode === "orphans"}
                        onClick={() => setSubMode("orphans")}
                        data-testid="attendance-sub-orphans"
                        className={`inline-flex items-center h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${subMode === "orphans" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                    >
                        Orphaned records
                    </button>
                </div>
            )}
            {subMode === "orphans" && isSuperAdminHere ? (
                <OrphanedAttendance users={users || []} onChanged={load} />
            ) : subMode === "chat" ? (
                <AttendanceChatClassifier visibleUsers={users || []} onPublished={load} />
            ) : (
            <>
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h3 className="font-heading text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
                            <Clock className="h-5 w-5 text-[#2EB4AC]" /> Monthly attendance rollup
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                            Download the sample template, fill it, and upload. Employees are matched by first name within their department.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <select
                            value={monthFilter}
                            onChange={(e) => setMonthFilter(e.target.value)}
                            data-testid="cp-attendance-month-filter"
                            className="h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:border-[#2EB4AC]"
                        >
                            <option value="all">All months</option>
                            {months.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
                        </select>
                        {picked.length === 0 && (
                            <div className="hidden md:inline-flex items-center gap-1 text-[11px] text-slate-400">
                                <kbd className="inline-flex items-center px-1 h-4 rounded bg-slate-100 border border-slate-200 font-mono text-[10px]">↑↓</kbd>
                                navigate
                                <kbd className="inline-flex items-center px-1 h-4 rounded bg-slate-100 border border-slate-200 font-mono text-[10px] ml-1.5">Space</kbd>
                                select
                            </div>
                        )}
                        <BulkUploader endpoint="/attendance" entity="attendance" onDone={load} />
                        <ExportAttendanceMenu
                            filtered={filtered}
                            monthFilter={monthFilter}
                            fmtMonth={fmtMonth}
                        />
                        {isSuperAdminHere && (
                            <button
                                type="button"
                                onClick={() => setShowNotifyDialog(true)}
                                data-testid="cp-attendance-notify-btn"
                                disabled={monthFilter === "all"}
                                title={monthFilter === "all"
                                    ? "Pick a specific month to notify managers about"
                                    : `Email managers & managees the ${fmtMonth(monthFilter)} rollup`}
                                className="inline-flex items-center gap-2 h-11 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Mail className="h-4 w-4" />
                                Notify managers
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setShowAddDialog(true)}
                            data-testid="cp-add-attendance-row-btn"
                            className="inline-flex items-center gap-2 h-11 px-3 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white font-medium text-sm transition-colors"
                            title="Add a single attendance row (useful when bulk upload missed someone, e.g. yourself)"
                        >
                            <Plus className="h-4 w-4" /> Add row
                        </button>
                    </div>
                </div>

                <div className="mt-5 border border-slate-200 rounded-lg overflow-hidden">
                    <div ref={tableRef} tabIndex={0} className="overflow-x-auto max-h-[520px] overflow-y-auto scrollbar-thin focus:outline-none">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    <th className="px-3 py-2 w-10">
                                        <input
                                            type="checkbox"
                                            checked={allPicked}
                                            ref={(el) => { if (el) el.indeterminate = somePicked; }}
                                            onChange={toggleAll}
                                            data-testid="select-all-attendance"
                                            className="h-4 w-4 accent-[#2EB4AC] cursor-pointer"
                                            aria-label="Select all"
                                        />
                                    </th>
                                    <th className="px-3 py-2">Employee</th>
                                    <th className="px-3 py-2">Dept</th>
                                    <th className="px-3 py-2">Month</th>
                                    <th className="px-3 py-2 text-right">Total</th>
                                    <th className="px-3 py-2 text-right">OFF</th>
                                    <th className="px-3 py-2 text-right">WFH</th>
                                    <th className="px-3 py-2 text-right">Uploaded by</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading ? (
                                    <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500 text-sm">No records yet — upload the first monthly rollup to get started.</td></tr>
                                ) : (
                                    filtered.map((r, idx) => {
                                        const rk = rowKey(r);
                                        const isPicked = picked.includes(rk);
                                        const isFocused = idx === focused;
                                        return (
                                            <tr
                                                key={rk}
                                                className={`${isPicked ? "bg-teal-50/40" : isFocused ? "bg-slate-100/70 ring-1 ring-inset ring-[#2EB4AC]/30" : "hover:bg-slate-50/60"} transition-colors`}
                                                onClick={() => setFocused(idx)}
                                            >
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={isPicked}
                                                        onChange={() => togglePick(rk)}
                                                        data-testid={`select-attendance-${rk}`}
                                                        className="h-4 w-4 accent-[#2EB4AC] cursor-pointer"
                                                        aria-label={`Select ${r.employee_name} ${r.month}`}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-slate-900 font-medium">
                                                    {r.employee_name}
                                                    {r.shift && (
                                                        <span
                                                            data-testid={`shift-badge-${rk}`}
                                                            className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${
                                                                r.shift === "firmware"
                                                                    ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                                                    : "bg-slate-50 text-slate-600 border-slate-200"
                                                            }`}
                                                            title={r.shift === "firmware" ? "Firmware Shift · 09:00-18:00" : "Corporate Shift · 10:00-19:00"}
                                                        >
                                                            {r.shift === "firmware" ? "9-6" : "10-7"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-slate-600 text-xs">{r.department_name || "—"}</td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-[#25908A] border border-teal-100">{fmtMonth(r.month)}</span>
                                                </td>
                                                <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.total_instances || 0}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.oof || 0}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.wfh || 0}</td>
                                                <td className="px-3 py-2 text-right text-xs text-slate-500">{(r.uploaded_by || "").split("@")[0]}</td>
                                                <td className="px-3 py-2 text-right">
                                                    <button
                                                        onClick={() => confirmDelete(r)}
                                                        data-testid={`delete-attendance-${r.id}`}
                                                        className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
                                                        aria-label="Delete"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Floating bulk action toolbar */}
            {picked.length > 0 && (
                <div
                    className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 flex items-center gap-2 h-12 pl-4 pr-2 rounded-full bg-slate-900 text-white shadow-2xl border border-slate-700 animate-in fade-in slide-in-from-bottom-4 duration-200"
                    data-testid="bulk-action-toolbar"
                >
                    <div className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-[#2EB4AC] text-white text-xs font-bold tabular-nums">
                        {picked.length}
                    </div>
                    <span className="text-sm text-slate-200">selected</span>
                    <div className="w-px h-6 bg-slate-700 mx-1" />
                    <button
                        onClick={() => setPicked([])}
                        className="h-8 px-3 rounded-full text-slate-300 hover:bg-slate-800 text-xs font-medium"
                        data-testid="clear-selection-btn"
                    >
                        Clear <kbd className="ml-1 text-[10px] font-mono opacity-70">Esc</kbd>
                    </button>
                    <button
                        onClick={bulkDelete}
                        data-testid="bulk-delete-attendance-btn"
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-red-500/90 hover:bg-red-500 text-white text-xs font-semibold"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                    </button>
                </div>
            )}

            </>
            )}

            {showAddDialog && (
                <AddAttendanceDialog
                    visibleUsers={users || []}
                    onClose={() => setShowAddDialog(false)}
                    onCreated={async () => {
                        setShowAddDialog(false);
                        try {
                            await load();
                            toast.success("Attendance row added");
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.error("Reload after add failed:", e);
                            toast.success("Attendance row added — reload to see it.");
                        }
                    }}
                />
            )}
            {showNotifyDialog && (
                <NotifyManagersDialog
                    month={monthFilter}
                    fmtMonth={fmtMonth}
                    onClose={() => setShowNotifyDialog(false)}
                />
            )}
        </div>
    );
};

/** KNO tab — bulk upload + quick link to marking grid + self-service toggle */

// ---------- Ticker tab (super admin only) ----------
// Manages the moving "Live notice" ticker that scrolls across Dashboard +
// Attendance. Each row is { icon, text }. The ticker can also be turned
// off entirely with the enabled toggle. Drag-handle reorders rows.
const TickerTab = ({ settings, onChange }) => {
    const [enabled, setEnabled] = useState(true);
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);
    const [draggingIndex, setDraggingIndex] = useState(null);

    useEffect(() => {
        if (!settings) return;
        const list = Array.isArray(settings.ticker_messages) ? settings.ticker_messages : [];
        setItems(list.map((m, i) => ({ id: `m_${i}_${Date.now()}`, icon: m.icon || "ℹ", text: m.text || "" })));
        setEnabled(settings.ticker_enabled !== false);
    }, [settings]);

    const update = (id, patch) => setItems((arr) => arr.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    const remove = (id) => setItems((arr) => arr.filter((m) => m.id !== id));
    const add = () => setItems((arr) => [...arr, { id: `m_new_${Date.now()}`, icon: "✨", text: "" }]);

    const onDragStart = (i) => setDraggingIndex(i);
    const onDragOver = (e) => e.preventDefault();
    const onDrop = (i) => {
        if (draggingIndex == null || draggingIndex === i) return;
        setItems((arr) => {
            const next = [...arr];
            const [moved] = next.splice(draggingIndex, 1);
            next.splice(i, 0, moved);
            return next;
        });
        setDraggingIndex(null);
    };

    const save = async () => {
        // Strip empties before persisting
        const ticker_messages = items
            .map((m) => ({ icon: (m.icon || "").trim(), text: (m.text || "").trim() }))
            .filter((m) => m.text);
        setSaving(true);
        try {
            await api.put("/settings", { ticker_messages, ticker_enabled: enabled });
            toast.success("Ticker saved — visible to everyone on Dashboard & Attendance");
            await onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
            <div className="p-6 border-b border-slate-100">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h3 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
                            <Megaphone className="h-5 w-5 text-[#2EB4AC]" />
                            Moving ticker
                        </h3>
                        <p className="text-sm text-slate-500 mt-1 max-w-xl">
                            One-line announcements that scroll across the Dashboard and Attendance page. Use the drag handle to reorder, or toggle the visibility switch to pause the ticker without losing your messages.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setEnabled((v) => !v)}
                        data-testid="ticker-toggle"
                        className={`inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold border transition-colors ${
                            enabled
                                ? "bg-teal-50 border-teal-200 text-[#1F7F79]"
                                : "bg-slate-50 border-slate-200 text-slate-600"
                        }`}
                    >
                        {enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        {enabled ? "Visible to everyone" : "Hidden"}
                    </button>
                </div>
            </div>

            <div className="p-6 space-y-3">
                {items.length === 0 ? (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                        <Megaphone className="h-8 w-8 text-slate-300 mx-auto" />
                        <p className="mt-3 font-medium text-slate-700">No messages yet</p>
                        <p className="text-sm text-slate-500 mt-1">Add at least one message to bring the ticker back.</p>
                    </div>
                ) : (
                    items.map((m, i) => (
                        <div
                            key={m.id}
                            draggable
                            onDragStart={() => onDragStart(i)}
                            onDragOver={onDragOver}
                            onDrop={() => onDrop(i)}
                            className={`flex items-center gap-2 p-2 border rounded-lg bg-white transition-colors ${
                                draggingIndex === i ? "border-[#2EB4AC] opacity-60" : "border-slate-200 hover:border-slate-300"
                            }`}
                        >
                            <GripVertical className="h-4 w-4 text-slate-300 cursor-grab shrink-0" aria-label="Drag to reorder" />
                            <input
                                type="text"
                                value={m.icon}
                                onChange={(e) => update(m.id, { icon: e.target.value })}
                                placeholder="✨"
                                aria-label="Icon"
                                maxLength={4}
                                data-testid={`ticker-icon-${i}`}
                                className="w-12 h-10 px-2 text-center text-lg border border-slate-200 rounded-md focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/30 outline-none"
                            />
                            <input
                                type="text"
                                value={m.text}
                                onChange={(e) => update(m.id, { text: e.target.value })}
                                placeholder="Type the announcement…"
                                aria-label="Message text"
                                data-testid={`ticker-text-${i}`}
                                className="flex-1 h-10 px-3 border border-slate-200 rounded-md text-sm focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/30 outline-none"
                            />
                            <button
                                type="button"
                                onClick={() => remove(m.id)}
                                aria-label="Remove message"
                                data-testid={`ticker-remove-${i}`}
                                className="h-10 w-10 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    ))
                )}
                <button
                    type="button"
                    onClick={add}
                    data-testid="ticker-add-btn"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-dashed border-slate-300 text-slate-600 hover:border-[#2EB4AC] hover:text-[#25908A] text-sm font-medium transition-colors"
                >
                    <Plus className="h-4 w-4" /> Add message
                </button>

                {/* Preview */}
                <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Live preview</div>
                    {enabled && items.filter((m) => (m.text || "").trim()).length > 0 ? (
                        <div className="relative overflow-hidden rounded-lg border border-teal-200 bg-gradient-to-r from-teal-50 via-white to-teal-50">
                            <div className="flex items-center gap-12 py-3 px-4 whitespace-nowrap text-sm text-slate-700">
                                {items.filter((m) => (m.text || "").trim()).map((m, i) => (
                                    <span key={i} className="inline-flex items-center gap-2">
                                        <span className="text-base">{m.icon || "•"}</span>
                                        <span className="font-medium">{m.text}</span>
                                        <span className="text-slate-300 mx-2">·</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 italic">
                            Ticker is hidden — the Dashboard and Attendance pages will skip rendering it.
                        </div>
                    )}
                </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="ticker-save-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white font-semibold disabled:opacity-50"
                >
                    {saving ? <Save className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save ticker
                </button>
            </div>
        </div>
    );
};

// ============= Learning AI tab =============
// Org-wide batch refine. Two scan modes share the same Before/After
// preview UI: "Collections" calls /api/ai/refine-collections-batch and
// targets {name, description}; "Resources" calls /api/ai/refine-
// resources-batch and targets only the resource name. Nothing is
// persisted until the admin ticks rows and clicks "Apply N".


// ============================ Email Alerts ============================
// Super-admin tab to configure & manually trigger inactivity nudges.
// Two main panels:
//   1) Settings  — master toggle, thresholds, test email
//   2) Lapsed    — table of candidates with eligibility, per-user mute
const EmailAlertsTab = ({ settings, users, onChange }) => {
    const [enabled, setEnabled] = useState(false);
    const [threshold, setThreshold] = useState(7);
    const [repeat, setRepeat] = useState(7);
    const [testEmail, setTestEmail] = useState("");
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [sendingLive, setSendingLive] = useState(false);
    const [confirmLive, setConfirmLive] = useState(false);

    // Hydrate local state from server settings
    useEffect(() => {
        if (!settings) return;
        setEnabled(!!settings.inactivity_alerts_enabled);
        setThreshold(settings.inactivity_threshold_days ?? 7);
        setRepeat(settings.inactivity_repeat_days ?? 7);
        setTestEmail(settings.inactivity_test_email || "");
    }, [settings]);

    const loadPreview = async () => {
        setLoadingPreview(true);
        try {
            const r = await api.get("/inactivity-alerts/preview");
            setPreview(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load preview");
        } finally { setLoadingPreview(false); }
    };

    useEffect(() => { loadPreview(); }, []);

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            await api.put("/settings", {
                inactivity_alerts_enabled: enabled,
                inactivity_threshold_days: Number(threshold) || 7,
                inactivity_repeat_days: Number(repeat) || 7,
                inactivity_test_email: testEmail.trim(),
            });
            toast.success("Saved");
            await onChange?.();
            await loadPreview();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSavingSettings(false); }
    };

    const sendTest = async () => {
        if (!testEmail.trim()) { toast.error("Add a test email first"); return; }
        setSendingTest(true);
        try {
            const r = await api.post("/inactivity-alerts/send", { test_only: true });
            toast.success(`Test sent to ${r.data?.sent_to}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test send failed");
        } finally { setSendingTest(false); }
    };

    const sendLive = async () => {
        if (!enabled) { toast.error("Enable alerts first"); return; }
        if (!confirmLive) { setConfirmLive(true); return; }
        setSendingLive(true);
        try {
            const r = await api.post("/inactivity-alerts/send", { test_only: false });
            const { sent, failed, eligible } = r.data || {};
            if (failed > 0) toast.error(`Sent ${sent}/${eligible} — ${failed} failed`);
            else toast.success(`Sent ${sent} ${sent === 1 ? "nudge" : "nudges"}`);
            setConfirmLive(false);
            await loadPreview();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Live send failed");
        } finally { setSendingLive(false); }
    };

    const toggleMute = async (u) => {
        try {
            await api.put(`/users/${u.user_id}`, { inactivity_alerts_disabled: !u.muted });
            toast.success(u.muted ? "Alerts unmuted" : "Alerts muted");
            await loadPreview();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update");
        }
    };

    const candidates = preview?.candidates || [];
    const eligibleCount = preview?.eligible_count || 0;
    const totalActive = users?.filter((u) => u.active !== false).length || 0;
    const everSeen = candidates.length + (totalActive - candidates.length); // best effort

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[#2EB4AC]" />
                    Email alerts · Inactivity nudges
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Gently nudge teammates who haven&apos;t dropped into Odyssey for a while.
                    Sent via Gmail SMTP from <span className="font-mono text-slate-700">sweekar@wellversed.in</span>.
                </p>
            </div>

            {/* Settings card */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 sm:p-6 space-y-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Master switch</div>
                        <div className="mt-1 text-sm text-slate-700 max-w-md">
                            When on, the manual <strong>Send now</strong> button will email every eligible lapsed user.
                            When off, only test emails are allowed.
                        </div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <span className={`text-xs font-semibold ${enabled ? "text-[#25908A]" : "text-slate-400"}`}>
                            {enabled ? "ENABLED" : "DISABLED"}
                        </span>
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            data-testid="email-alerts-enabled-toggle"
                            className="h-5 w-9 appearance-none rounded-full bg-slate-200 checked:bg-[#2EB4AC] relative cursor-pointer transition-colors
                                       before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:shadow before:transition-transform
                                       checked:before:translate-x-4"
                        />
                    </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Inactivity threshold</label>
                        <div className="relative">
                            <input
                                type="number"
                                min="1"
                                max="120"
                                value={threshold}
                                onChange={(e) => setThreshold(e.target.value)}
                                data-testid="email-alerts-threshold-input"
                                className="h-10 w-full px-3 pr-12 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] outline-none tabular-nums"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">days</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">Nudge after this many days of no activity.</div>
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Re-alert every</label>
                        <div className="relative">
                            <input
                                type="number"
                                min="1"
                                max="120"
                                value={repeat}
                                onChange={(e) => setRepeat(e.target.value)}
                                data-testid="email-alerts-repeat-input"
                                className="h-10 w-full px-3 pr-12 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] outline-none tabular-nums"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">days</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">Keep nudging this often while they stay away.</div>
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Test email</label>
                        <input
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="sweekar@wellversed.in"
                            data-testid="email-alerts-test-email-input"
                            className="h-10 w-full px-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] outline-none"
                        />
                        <div className="text-[11px] text-slate-500 mt-1">&ldquo;Send test&rdquo; routes only to this address.</div>
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        onClick={saveSettings}
                        disabled={savingSettings}
                        data-testid="email-alerts-save-btn"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-60 transition-colors"
                    >
                        {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save settings
                    </button>
                    <button
                        onClick={sendTest}
                        disabled={sendingTest || !testEmail.trim()}
                        data-testid="email-alerts-send-test-btn"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 hover:border-[#2EB4AC] text-slate-700 hover:text-[#25908A] text-sm font-semibold disabled:opacity-50 transition-colors"
                    >
                        {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        Send test (to me only)
                    </button>
                    <button
                        onClick={sendLive}
                        disabled={sendingLive || !enabled || eligibleCount === 0}
                        data-testid="email-alerts-send-live-btn"
                        className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                            confirmLive
                                ? "bg-red-600 hover:bg-red-700 text-white"
                                : "bg-slate-900 hover:bg-slate-800 text-white"
                        }`}
                        title={!enabled ? "Enable alerts first" : eligibleCount === 0 ? "No eligible users to nudge" : ""}
                    >
                        {sendingLive ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        {confirmLive ? `Confirm — email ${eligibleCount} ${eligibleCount === 1 ? "user" : "users"}` : `Send now to ${eligibleCount} ${eligibleCount === 1 ? "user" : "users"}`}
                    </button>
                    {confirmLive && (
                        <button
                            onClick={() => setConfirmLive(false)}
                            className="text-xs text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                        >
                            cancel
                        </button>
                    )}
                </div>

                {preview && !preview.smtp_configured && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 inline-flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        SMTP isn&apos;t configured on this server — set SMTP_USER / SMTP_APP_PASSWORD in <span className="font-mono">backend/.env</span> and restart.
                    </div>
                )}
            </div>

            {/* Lapsed users table */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-heading text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#2EB4AC]" />
                        Lapsed users
                        <span className="text-xs font-normal text-slate-500">
                            · {candidates.length} over threshold · {eligibleCount} ready to nudge
                        </span>
                    </h3>
                    <button
                        onClick={loadPreview}
                        data-testid="email-alerts-refresh-btn"
                        className="text-xs font-semibold text-slate-500 hover:text-[#2EB4AC] underline-offset-2 hover:underline"
                    >
                        Refresh
                    </button>
                </div>
                {loadingPreview ? (
                    <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
                ) : candidates.length === 0 ? (
                    <div className="p-10 text-center">
                        <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                        <div className="text-sm font-semibold text-slate-900">Everyone&apos;s been active recently</div>
                        <div className="text-xs text-slate-500 mt-1">No one is past the {threshold}-day threshold.</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="lapsed-users-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                <tr>
                                    <th className="text-left px-4 py-3">Employee</th>
                                    <th className="text-left px-4 py-3">Email</th>
                                    <th className="text-left px-4 py-3">Last seen</th>
                                    <th className="text-left px-4 py-3">Days away</th>
                                    <th className="text-left px-4 py-3">Last nudged</th>
                                    <th className="text-left px-4 py-3">Status</th>
                                    <th className="text-right px-4 py-3">Mute</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map((r) => (
                                    <tr key={r.user_id} className="border-t border-slate-100 hover:bg-slate-50/60">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className="h-7 w-7 rounded-full bg-teal-50 text-[#25908A] border border-teal-100 text-[10px] font-bold inline-flex items-center justify-center uppercase">
                                                    {(r.name || "?").charAt(0)}
                                                </span>
                                                <span className="font-medium text-slate-900">{r.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 font-mono text-xs">{r.email}</td>
                                        <td className="px-4 py-3 text-slate-600 tabular-nums">{formatRelative(r.last_seen_at)}</td>
                                        <td className="px-4 py-3 tabular-nums">
                                            <span className={`inline-flex items-center px-2 h-6 rounded-full text-xs font-semibold ${
                                                r.days_away >= 30 ? "bg-red-100 text-red-700"
                                                : r.days_away >= 14 ? "bg-amber-100 text-amber-800"
                                                : "bg-slate-100 text-slate-700"
                                            }`}>
                                                {r.days_away}d
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 tabular-nums">{r.last_alert_at ? formatRelative(r.last_alert_at) : <span className="text-slate-400">never</span>}</td>
                                        <td className="px-4 py-3">
                                            {r.muted ? (
                                                <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                                                    <BellOff className="h-3 w-3" /> Muted
                                                </span>
                                            ) : r.eligible_for_alert ? (
                                                <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                                                    Ready
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                                    Recently nudged
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => toggleMute(r)}
                                                data-testid={`mute-toggle-${r.user_id}`}
                                                className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold transition-colors ${
                                                    r.muted
                                                        ? "bg-teal-50 text-[#25908A] hover:bg-teal-100"
                                                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                                }`}
                                                title={r.muted ? "Click to unmute" : "Click to mute alerts for this user"}
                                            >
                                                <BellOff className="h-3 w-3" />
                                                {r.muted ? "Unmute" : "Mute"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------- Learning · Sync tab ----------
// Housing for the previously-Learning-Hub-header tools: Backup widget
// (offline snapshot + Google Drive sync) and Bulk collections import
// via CSV/paste. Moved here so the Learning Hub itself stays a
// consumption surface — power-user knobs live in the Control Panel.
const LearningSyncTab = ({ user, depts }) => {
    const [showBulk, setShowBulk] = useState(false);
    const { settings, refreshAll } = useData();
    const [togglingRevamp, setTogglingRevamp] = useState(false);
    const [togglingCovers, setTogglingCovers] = useState(false);
    const [backfillBusy, setBackfillBusy] = useState(false);
    // Default = revamped ON (true) so first-load matches the new experience.
    const revamped = settings?.learning_hub_revamped !== false;
    // Default = auto-covers OFF → collections start with solid dept-tinted
    // canvases; users generate a Nano Banana cover per collection on demand.
    const autoCovers = !!settings?.learning_auto_covers;
    const backfillCovers = async (force) => {
        if (backfillBusy) return;
        if (force && !window.confirm("Force regenerate covers for ALL collections? This calls Nano Banana once per collection and replaces every existing cover.")) return;
        setBackfillBusy(true);
        try {
            toast.loading(force ? "Regenerating every cover with Nano Banana…" : "Backfilling missing covers…", { id: "bf" });
            const r = await api.post(`/learning/collections/backfill-covers${force ? "?force=true" : ""}`);
            toast.success(force ? `Regenerated ${r.data.filled} covers` : `Filled ${r.data.filled} covers`, { id: "bf" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backfill failed", { id: "bf" });
        } finally { setBackfillBusy(false); }
    };
    const toggleRevamp = async () => {
        if (togglingRevamp) return;
        setTogglingRevamp(true);
        try {
            await api.put("/settings", { learning_hub_revamped: !revamped });
            await refreshAll?.();
            toast.success(!revamped ? "Revamped Learning Hub enabled for everyone" : "Reverted to the classic Learning Hub");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update setting");
        } finally { setTogglingRevamp(false); }
    };
    const toggleCovers = async () => {
        if (togglingCovers) return;
        setTogglingCovers(true);
        try {
            await api.put("/settings", { learning_auto_covers: !autoCovers });
            await refreshAll?.();
            toast.success(!autoCovers ? "Auto-generated covers enabled" : "Auto-generation off — collections show solid colors");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update setting");
        } finally { setTogglingCovers(false); }
    };
    return (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-6" data-testid="learning-sync-tab">
            <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                    <BookOpen className="h-6 w-6 text-[#2EB4AC]" />
                    Learning &middot; Sync &amp; Bulk import
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                    Snapshot the org&apos;s Learning Hub for offline safekeeping, wire up a Google Drive folder for continuous sync,
                    or bulk-create collections from a CSV / paste dump. These lived on the Learning Hub header before but they&apos;re admin tools, so they now live here.
                </p>
            </div>

            {/* Feature toggle — flip the whole org between the revamped
                (mockup-styled) Learning Hub and the classic one that
                shipped before this redesign session. */}
            <div className="border border-slate-200 rounded-xl p-5 bg-white" data-testid="learning-hub-revamp-toggle">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[280px]">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Feature flag</div>
                        <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">
                            Enable revamped Learning Hub
                        </h3>
                        <p className="text-sm text-slate-500 mt-1 max-w-lg">
                            When ON, everyone sees the new mockup-styled Hub (Discover, chip filters, cover images, drag-reorder, inline actions). When OFF, the classic pre-mockup Learning Hub is served instead. Change applies to all users org-wide.
                        </p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={revamped}
                        aria-label="Toggle revamped Learning Hub"
                        disabled={togglingRevamp}
                        onClick={toggleRevamp}
                        data-testid="learning-hub-revamp-switch"
                        className={`relative inline-flex items-center h-7 w-12 rounded-full transition-colors ${revamped ? "bg-[#2EB4AC]" : "bg-slate-300"} disabled:opacity-60`}
                    >
                        <span className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform ${revamped ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                    Current state: <span className="font-semibold text-slate-700">{revamped ? "Revamped (new)" : "Classic (legacy)"}</span>
                </div>
            </div>

            {/* Feature toggle — auto-generate Nano Banana covers for every
                collection vs. keep the solid dept-tinted canvas and let
                owners generate a cover per collection on demand. */}
            <div className="border border-slate-200 rounded-xl p-5 bg-white" data-testid="learning-auto-covers-toggle">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[280px]">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Feature flag</div>
                        <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">
                            Auto-generate covers
                        </h3>
                        <p className="text-sm text-slate-500 mt-1 max-w-lg">
                            When <span className="font-semibold">ON</span>, collections render their cover image (Nano Banana / URL) and admins get the Backfill Covers action below. When <span className="font-semibold">OFF</span> (default), collections show the original solid dept-tinted canvas and owners get a per-collection <span className="font-semibold">Generate cover</span> button that calls Nano Banana on demand — nothing auto-generates.
                        </p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={autoCovers}
                        aria-label="Toggle auto-generated covers"
                        disabled={togglingCovers}
                        onClick={toggleCovers}
                        data-testid="learning-auto-covers-switch"
                        className={`relative inline-flex items-center h-7 w-12 rounded-full transition-colors ${autoCovers ? "bg-[#2EB4AC]" : "bg-slate-300"} disabled:opacity-60`}
                    >
                        <span className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform ${autoCovers ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                    Current state: <span className="font-semibold text-slate-700">{autoCovers ? "Auto-generated covers" : "Solid colors + manual per-collection generate"}</span>
                </div>
            </div>

            {/* Backfill Covers — only relevant when auto-covers are enabled. */}
            {autoCovers && (
                <div className="border border-slate-200 rounded-xl p-5 bg-white" data-testid="learning-backfill-covers-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[280px]">
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Admin action</div>
                            <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">
                                Backfill collection covers
                            </h3>
                            <p className="text-sm text-slate-500 mt-1 max-w-lg">
                                Auto-generate Gemini Nano Banana covers for Learning Hub collections. Use <span className="font-semibold">Backfill Missing Only</span> to fill collections that don&apos;t have a cover yet, or <span className="font-semibold">Force Regenerate All</span> to replace every existing cover with a fresh one.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                disabled={backfillBusy}
                                onClick={() => backfillCovers(false)}
                                data-testid="cp-backfill-missing"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                            >
                                <Sparkles className="h-4 w-4 text-[#2EB4AC]" /> Backfill Missing Only
                            </button>
                            <button
                                type="button"
                                disabled={backfillBusy}
                                onClick={() => backfillCovers(true)}
                                data-testid="cp-backfill-force"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#2EB4AC] text-white hover:bg-[#25908A] disabled:opacity-60"
                            >
                                <RefreshCw className="h-4 w-4" /> Force Regenerate All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <BackupWidget user={user} depts={depts} />
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50/60">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Bulk import</div>
                        <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">Create many collections at once</h3>
                        <p className="text-sm text-slate-500 mt-1 max-w-lg">
                            Paste a CSV (two columns: name, description) or drop the template file. Duplicates are auto-skipped.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowBulk(true)}
                        data-testid="learning-sync-bulk-open"
                        className="inline-flex items-center gap-2 h-10 px-4 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors"
                    >
                        <UploadCloud className="h-4 w-4" /> Open bulk importer
                    </button>
                </div>
            </div>
            {showBulk && (
                <BulkCollectionsDialog
                    onClose={() => setShowBulk(false)}
                    onImported={() => setShowBulk(false)}
                />
            )}
        </div>
    );
};

// Short, friendly relative-time helper for the lapsed-users table.
const formatRelative = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const day = 86400000;
        const days = Math.floor(diffMs / day);
        if (days <= 0) return "today";
        if (days === 1) return "yesterday";
        if (days < 30) return `${days}d ago`;
        if (days < 365) return `${Math.floor(days / 30)}mo ago`;
        return `${Math.floor(days / 365)}y ago`;
    } catch { return iso; }
};


export default ControlPanel;
